import React, { useCallback, useEffect, useState } from 'react';
import { Check, Loader2, Monitor, TriangleAlert } from 'lucide-react';
import { useSimulation } from '../../context/SimulationContext';

const TROUBLE_MSG =
  "Windows could not automatically detect this network's proxy settings";

export function NetworkSettings({
  windowId: _windowId,
}: {
  windowId: string;
}): React.ReactElement {
  const { state, logAction } = useSimulation();
  const { isFixed } = state;

  useEffect(() => {
    logAction('opened_network_settings');
  }, [logAction]);
  const [showProps, setShowProps] = useState(false);
  const [troubleLoading, setTroubleLoading] = useState(false);
  const [troubleDone, setTroubleDone] = useState(false);

  const openProps = useCallback(() => {
    setShowProps(true);
    logAction('viewed_network_properties');
    if (!isFixed) {
      logAction('identified_apipa_address');
    }
  }, [isFixed, logAction]);

  const runTroubleshooter = useCallback(() => {
    setTroubleDone(false);
    setTroubleLoading(true);
    logAction('ran_troubleshooter');
    window.setTimeout(() => {
      setTroubleLoading(false);
      setTroubleDone(true);
    }, 3000);
  }, [logAction]);

  return (
    <div
      className="flex flex-1 min-h-0 flex-col bg-black/20 text-white/95"
      data-tutorial="network-settings-focus"
    >
      <div className="border-b border-white/10 px-4 py-3 flex items-center gap-2">
        <Monitor className="h-5 w-5 text-white/80" aria-hidden />
        <h1 className="text-base font-semibold tracking-tight">
          Network &amp; Internet
        </h1>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        <section className="rounded-xl border border-white/15 bg-white/5 p-4">
          <h2 className="text-sm font-medium text-white/90 mb-3">Wi-Fi</h2>
          <div className="flex items-start gap-3">
            <div className="mt-0.5">
              {isFixed ? (
                <Check
                  className="h-6 w-6 text-emerald-400"
                  aria-hidden
                  strokeWidth={2.5}
                />
              ) : (
                <TriangleAlert
                  className="h-6 w-6 text-amber-400"
                  aria-hidden
                />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium">
                {isFixed
                  ? 'Office-WiFi — Connected'
                  : 'Office-WiFi — No internet access'}
              </p>
              <p className="text-sm text-white/65 mt-1">
                {isFixed
                  ? 'Connected, secured'
                  : 'Connected, no internet'}
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  className="rounded-lg border border-white/20 bg-white/10 px-3 py-1.5 text-sm hover:bg-white/15 transition-colors"
                  onClick={openProps}
                >
                  Properties
                </button>
                <button
                  type="button"
                  className="rounded-lg border border-white/20 bg-white/10 px-3 py-1.5 text-sm hover:bg-white/15 transition-colors disabled:opacity-60"
                  onClick={runTroubleshooter}
                  disabled={troubleLoading}
                >
                  {troubleLoading ? (
                    <span className="inline-flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Working…
                    </span>
                  ) : (
                    'Troubleshoot'
                  )}
                </button>
              </div>
              {troubleDone && (
                <p className="mt-3 text-xs text-amber-200/90 leading-relaxed">
                  {TROUBLE_MSG}
                </p>
              )}
            </div>
          </div>
        </section>
      </div>

      {showProps && (
        <NetworkPropertiesPanel
          isFixed={isFixed}
          onClose={() => setShowProps(false)}
        />
      )}
    </div>
  );
}

function NetworkPropertiesPanel({
  isFixed,
  onClose,
}: {
  isFixed: boolean;
  onClose: () => void;
}): React.ReactElement {
  return (
    <div
      className="absolute inset-0 z-10 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4 animate-fade-in"
      role="dialog"
      aria-modal="true"
      aria-label="Network properties"
    >
      <div className="w-full max-w-md rounded-xl border border-white/20 bg-[#1c1c2e]/95 backdrop-blur-xl shadow-2xl">
        <div className="border-b border-white/10 px-4 py-3 flex items-center justify-between">
          <h3 className="font-semibold text-sm">Office-WiFi properties</h3>
          <button
            type="button"
            className="rounded-md px-2 py-1 text-xs text-white/70 hover:bg-white/10"
            onClick={onClose}
          >
            Close
          </button>
        </div>
        <div className="p-4 space-y-2 text-sm font-mono text-white/90">
          {isFixed ? (
            <>
              <Row label="IPv4 address" value="192.168.1.45" />
              <Row label="Subnet mask" value="255.255.255.0" />
              <Row label="Default gateway" value="192.168.1.1" />
              <Row label="DNS servers" value="8.8.8.8" />
            </>
          ) : (
            <>
              <Row label="IPv4 address" value="169.254.47.23" />
              <Row label="Subnet mask" value="255.255.0.0" />
              <Row label="Default gateway" value="(blank)" muted />
              <Row label="DNS servers" value="(blank)" muted />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({
  label,
  value,
  muted,
}: {
  label: string;
  value: string;
  muted?: boolean;
}): React.ReactElement {
  return (
    <div className="flex justify-between gap-4 border-b border-white/5 pb-2 last:border-0">
      <span className="text-white/55 shrink-0">{label}</span>
      <span className={muted ? 'text-white/40' : 'text-emerald-200/90'}>
        {value}
      </span>
    </div>
  );
}
